// Typed client for the app's API.

export type { Level, Metric, Question } from "../shared/types.ts";
export type * from "../shared/responses.ts";
import type { Level } from "../shared/types.ts";
import type { AppConfig, AskResult, Backtest, Centroids, GeoInfo, ListingExtract, ListingResponse, MapData, MetricsResponse, Periods, RankResult, Relation, Scores,
  ScreenResult, SearchResults, Series, Site, Underwrite, WhatIf, Zones } from "../shared/responses.ts";

/** The user's budget as query params (web/budget.ts), added to the requests that can carry a personal metric. */
let budgetQ = "";
export function setBudgetQuery(q: string) {
  budgetQ = q;
}
const withBudget = (path: string) => (budgetQ && /^\/api\/(map|series|rank|screen)\//.test(path) ? `${path}${path.includes("?") ? "&" : "?"}${budgetQ}` : path);

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(withBudget(path), { signal });
  if (!r.ok) {
    const msg = await r.text().catch(() => r.statusText);
    // the API's { error } message is written for people; anything else (a proxy's page) gets the status
    const err = (() => { try { return (JSON.parse(msg) as { error?: unknown }).error; } catch { return null; } })();
    throw new Error(typeof err === "string" ? err : `${r.status}: ${msg.slice(0, 200)}`);
  }
  return (await r.json()) as T;
}

let configOnce: Promise<AppConfig> | undefined;

export const api = {
  ask: async (question: string, context: { level: Level; metric: string; geo: string | null }): Promise<AskResult> => {
    const r = await fetch("/api/ask", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ question, context }) });
    const body = (await r.json().catch(() => ({}))) as AskResult & { error?: string; limit?: string };
    // limit "signup": the free questions without an account are used up (web/ask.ts opens the sign-up sheet)
    if (!r.ok) throw Object.assign(new Error(body.error ?? `${r.status}`), { limit: body.limit });
    return body;
  },
  /** pasted listing text → the listing analyzer's fields, each with the model's confidence (src/extract.ts) */
  extractListing: async (text: string): Promise<ListingExtract> => {
    const r = await fetch("/api/extract/listing", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text }) });
    const body = (await r.json().catch(() => ({}))) as ListingExtract & { error?: string; limit?: string };
    // limit "signup": the pastes without an account are used up (web/listing.ts offers the sign-up sheet)
    if (!r.ok) throw Object.assign(new Error(body.error ?? `${r.status}`), { limit: body.limit });
    return body;
  },
  /** "monthly update" sign-up for one area (src/subscribe.ts): the same answer whether or not the address already follows it */
  subscribe: async (email: string, geo: string): Promise<{ ok: true; message: string }> => {
    const r = await fetch("/api/subscribe", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, geo }) });
    const body = (await r.json().catch(() => ({}))) as { ok: true; message: string; error?: string };
    if (!r.ok) throw new Error(body.error ?? `${r.status}`);
    return body;
  },
  /** The server's settings: the same for the whole page load, so fetched once (again after a failure). */
  config: () => (configOnce ??= get<AppConfig>("/api/config").catch((e) => { configOnce = undefined; throw e; })),
  /** personal: also the metrics computed from the user's budget (the main app; the report and the iOS panel don't) */
  metrics: (personal = false) =>
    get<MetricsResponse>(`/api/metrics${personal ? "?personal=1" : ""}`),
  periods: (level: Level, metric: string) => get<Periods>(`/api/periods/${level}/${metric}`),
  map: (level: Level, metric: string, period?: string | null, signal?: AbortSignal) =>
    get<MapData>(`/api/map/${level}/${metric}${period ? `?period=${period}` : ""}`, signal),
  centroids: (level: Level) =>
    get<Centroids>(`/api/centroids/${level}`),
  geo: (id: string) => get<GeoInfo>(`/api/geo/${encodeURIComponent(id)}`),
  series: (id: string, metric: string, freq: "monthly" | "yearly") =>
    get<Series>(`/api/series/${encodeURIComponent(id)}/${metric}?freq=${freq}`),
  scores: (id: string) => get<Scores>(`/api/scores/${encodeURIComponent(id)}`),
  /** a ZIP's data for the listing analyzer (shared/listing.ts computes in the browser as the inputs change) */
  listing: (id: string, signal?: AbortSignal) =>
    get<ListingResponse>(`/api/listing/${encodeURIComponent(id)}`, signal),
  /** hazard zone pieces in a small box [w, s, e, n] (the listing analyzer's address map) */
  zones: (b: number[], signal?: AbortSignal) =>
    get<Zones>(`/api/zones?bbox=${b.map((v) => v.toFixed(5)).join(",")}`, signal),
  /** hazards at one address (src/api.ts Api.address): its census block's share in each zone */
  address: (address: string, signal?: AbortSignal) => get<Site>(`/api/address?address=${encodeURIComponent(address)}`, signal),
  underwrite: (id: string) => get<Underwrite>(`/api/underwrite/${encodeURIComponent(id)}`),
  /** rate null = the forecast month's actual rate */
  whatIf: (id: string, rate: number | null) => get<WhatIf>(`/api/whatif/${encodeURIComponent(id)}${rate == null ? "" : `?rate=${rate}`}`),
  rank: (level: Level, metric: string, p: { within?: string | null; sort?: string; cols?: string[]; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (p.within) q.set("within", p.within);
    if (p.sort) q.set("sort", p.sort);
    if (p.cols?.length) q.set("cols", p.cols.join(","));
    q.set("limit", String(p.limit ?? 100));
    q.set("offset", String(p.offset ?? 0));
    return get<RankResult>(`/api/rank/${level}/${metric}?${q}`);
  },
  relate: (level: Level, x: string, y: string, within: string | null) =>
    get<Relation>(`/api/relate/${level}/${x}/${y}${within ? `?within=${encodeURIComponent(within)}` : ""}`),
  rankCsvUrl: (level: Level, metric: string, within: string | null, cols: string[]) =>
    withBudget(`/api/rank/${level}/${metric}?format=csv${within ? `&within=${within}` : ""}${cols.length ? `&cols=${cols.join(",")}` : ""}`),
  /** q: a screen's params (web/screen.ts screenParams) */
  screen: (level: Level, q: URLSearchParams, p: { cols?: string[]; limit?: number; offset?: number; codes?: boolean }, signal?: AbortSignal) => {
    const x = new URLSearchParams(q);
    if (p.cols?.length) x.set("cols", p.cols.join(","));
    x.set("limit", String(p.limit ?? 100));
    x.set("offset", String(p.offset ?? 0));
    if (p.codes) x.set("codes", "1");
    return get<ScreenResult>(`/api/screen/${level}?${x}`, signal);
  },
  screenCsvUrl: (level: Level, q: URLSearchParams, cols: string[]) => {
    const x = new URLSearchParams(q);
    if (cols.length) x.set("cols", cols.join(","));
    x.set("format", "csv");
    return withBudget(`/api/screen/${level}?${x}`);
  },
  search: (q: string, signal?: AbortSignal) =>
    get<SearchResults>(`/api/search?q=${encodeURIComponent(q)}`, signal),
  backtest: () => get<Backtest>("/api/backtest"),
};
